import PropTypes from "prop-types";
import * as React from "react";
import ReactDOM from "react-dom";

import { detectIE } from "../../../Engine/utils";
import { uuid } from "../../../../../Common/UUID";
import { BindingPath } from "../../../Engine/Core/CoreTypes";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { NAVIGATION_WIDTH, PAGE_PADDING, WIDE_NAVIGATION_WIDTH } from "../../constants";
import { UnitLayoutingContext } from "../../FormParts/Unit/Internals/UnitBorderContext/UnitLayoutingContext";
import { unitIdContentTypes, UnitIdContext } from "../../FormParts/Unit/UnitIdContext";
import { LayoutEvents, LayoutEventsSubscription } from "../../Internals/LayoutEvents/LayoutEvents";
import { emptyRef, pullContext } from "../../TypingUtils";
import { DataPasteContext, DatePasteStatus, RegisterTableDataPasteFunction } from "../MultiLine/TableDataPasteContext";
import { ExcelPasteUtils } from "../MultiLine/ExcelPasteUtils";
import { arrayHasLength, reject } from "../../../../../Common/TypingUtils";
import { UnitId } from "../../FormParts/Unit/UnitId/UnitId";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";

import { StickyTable } from "./StickyTable";

export interface StickyTableWrapperProps {
    children: React.ReactNode;
    columnWidth: number[];
    footerHeight?: number;
    isHeaderFixed: boolean;
    isWidePanel: boolean;
    maxWidth: undefined | number;
    unitLayouting: UnitLayoutingContext;
    panelState: string;
    setUnitTableWidth: (tableId: string, unitId: UnitId, width: number) => void;
    removeUnitTableWidth: (tableId: string, unitId: UnitId) => void;
    sideSize: number;
    width?: number;
    fillMultiline: (bindingPath: BindingPath, values: ModelHunk, newInstanceCount: number) => void;
    "data-tid"?: string;
    optional: boolean;
}

interface StickyTableWrapperState {
    heightRows?: number[];
    left: number;
    width?: number;
}

type StickyTableWrapperContext = UnitIdContext;

interface StickyTableWrapperChildContext {
    setPageMaxWidth?: (x: number) => void;
    recalcHeightRows: () => void;
    setSidebarBody: (sidebarBody: null | HTMLTableSectionElement) => void;
    getFooterHeight: () => void;
    setDataBody: (dataBody: null | HTMLElement) => void;
    registerRowDataPasteFunc: (func: RegisterTableDataPasteFunction, instanceIndex: number) => void;
    processTableDataPaste: (
        clipboardContent: DataTransfer,
        context: DataPasteContext,
        startIndex: number
    ) => DatePasteStatus;
    fillMultiline: (bindingPath: BindingPath, values: ModelHunk, newInstanceCount: number) => void;
}

export class StickyTableWrapper extends React.PureComponent<StickyTableWrapperProps, StickyTableWrapperState> {
    public static defaultProps = {
        footerHeight: 80,
    };

    public static childContextTypes = {
        recalcHeightRows: PropTypes.func,
        getFooterHeight: PropTypes.func,
        setSidebarBody: PropTypes.func,
        setDataBody: PropTypes.func,
        registerRowDataPasteFunc: PropTypes.func,
        processTableDataPaste: PropTypes.func,
        fillMultiline: PropTypes.func,
    };

    public static contextTypes = { ...unitIdContentTypes };

    public stickyTable: null | StickyTable = emptyRef;
    public sidebarBody: null | HTMLTableSectionElement = emptyRef;
    public dataBody: null | HTMLElement = emptyRef;
    public tableId = `table-${uuid()}`;

    public initSidebarHeight = {};
    public state: StickyTableWrapperState = {
        left: NAVIGATION_WIDTH + PAGE_PADDING,
        heightRows: [],
    };

    private unitId?: UnitId;

    private layoutEventsSubscription?: LayoutEventsSubscription;
    private stickyTableDom: null | HTMLElement = emptyRef;
    public pastsFuncs: RegisterTableDataPasteFunction[] = [];

    private readonly widthMeasureAnchor = React.createRef<HTMLDivElement>();

    public getChildContext(): StickyTableWrapperChildContext {
        return {
            recalcHeightRows: () => {
                if (!document.documentElement || !this.stickyTableDom) {
                    return;
                }
                this.setState({ heightRows: this.getHeightRows() });
            },
            getFooterHeight: () => this.props.footerHeight,
            setSidebarBody: (sidebarBody: null | HTMLTableSectionElement) => {
                this.sidebarBody = sidebarBody;
            },
            setDataBody: (dataBody: null | HTMLElement) => {
                this.dataBody = dataBody;
            },
            registerRowDataPasteFunc: this.registerRowDataPasteFunc,
            processTableDataPaste: this.processTableDataPaste,
            fillMultiline: this.props.fillMultiline,
        };
    }

    public componentDidMount(): void {
        this.unitId = this.getUnitId();
        this.layoutEventsSubscription = LayoutEvents.addListener(this.sizeEvent);

        if (this.unitId != undefined) {
            this.props.setUnitTableWidth(this.tableId, this.unitId, this.props.maxWidth ?? NaN);
        }

        // All sticky elements must calc left, dont wrapper. this call rerender table elements
        const left = this.stickyTableDom ? this.stickyTableDom.getBoundingClientRect().left : this.state.left;

        // sync side and data rows height
        this.setState({
            left: left,
            heightRows: this.getHeightRows(true),
        });
    }

    public componentWillUnmount(): void {
        if (this.layoutEventsSubscription != undefined) {
            this.layoutEventsSubscription.remove();
        }

        if (this.unitId != undefined) {
            this.props.removeUnitTableWidth(this.tableId, this.unitId);
        }
    }

    public render(): JSX.Element {
        return (
            <>
                <div ref={this.widthMeasureAnchor} />
                <StickyTable
                    data-tid={this.props["data-tid"]}
                    columnWidth={this.props.columnWidth}
                    heightRows={this.state.heightRows}
                    isHeaderFixed={this.props.isHeaderFixed}
                    left={this.state.left}
                    ref={this.refSticky}
                    sideSize={this.props.sideSize}
                    width={this.getWidth()}
                    optional={this.props.optional}>
                    {this.props.children}
                </StickyTable>
            </>
        );
    }

    private readonly registerRowDataPasteFunc = (func: RegisterTableDataPasteFunction, instanceIndex: number): void => {
        this.pastsFuncs[instanceIndex] = func;
    };

    private readonly processTableDataPaste = (
        clipboardContent: DataTransfer,
        context: DataPasteContext,
        rowIndex?: number,
        columnIndex?: number
    ): DatePasteStatus => {
        const excelData = ExcelPasteUtils.parseClipboardContent(clipboardContent);
        const withoutEmptyEnding = ExcelPasteUtils.removeEmptyRowsAndColumns(excelData);

        if (
            arrayHasLength(withoutEmptyEnding, 0) ||
            (arrayHasLength(withoutEmptyEnding, 1) && arrayHasLength(withoutEmptyEnding[0], 1)) ||
            rowIndex == undefined ||
            columnIndex == undefined
        ) {
            return DatePasteStatus.Failure;
        }

        let currentRowIndex = rowIndex;
        for (let i = 0; i < withoutEmptyEnding.length; i++) {
            while (this.pastsFuncs[currentRowIndex] == undefined && currentRowIndex <= this.pastsFuncs.length) {
                currentRowIndex++;
            }
            if (currentRowIndex >= this.pastsFuncs.length) {
                return DatePasteStatus.SuccessAll;
            }
            if (this.pastsFuncs[currentRowIndex] != undefined) {
                const resultStatus = (this.pastsFuncs[currentRowIndex] ?? reject())(
                    withoutEmptyEnding.slice(i),
                    context,
                    columnIndex
                );
                if (resultStatus === DatePasteStatus.SuccessAll) {
                    return DatePasteStatus.SuccessAll;
                }
                currentRowIndex++;
            } else {
                return DatePasteStatus.Failure;
            }
        }
        return DatePasteStatus.SuccessAll;
    };

    private getUnitId(): UnitId | undefined {
        return this.getContext().getUnitId?.();
    }

    private getContext(): StickyTableWrapperContext {
        return pullContext<StickyTableWrapperContext>(this);
    }

    private readonly sizeEvent = () => {
        if (!document.documentElement || !this.stickyTableDom) {
            return;
        }

        const rect = this.stickyTableDom.getBoundingClientRect();
        const heightScreen = document.documentElement.offsetHeight;
        const widthScreen = document.documentElement.offsetWidth;

        if (rect.top > heightScreen || rect.bottom < 0) {
            return;
        }

        this.setState({
            width: widthScreen,
            left: rect.left,
        });
    };

    private readonly refSticky = (el: null | StickyTable) => {
        if (!el) {
            return;
        }

        this.stickyTableDom = toHtmlElementOrNull(ReactDOM.findDOMNode(el));
    };

    private readonly getWidth = (): number => {
        const { panelState, isWidePanel, unitLayouting, maxWidth } = this.props;

        if (this.props.width) {
            return this.props.width;
        }

        let maxTableWidth = maxWidth;

        if (this.widthMeasureAnchor.current != undefined) {
            const pageMaxWidth =
                this.widthMeasureAnchor.current.getBoundingClientRect().width +
                unitLayouting.paddingRight +
                unitLayouting.paddingLeft;
            maxTableWidth = Math.min(pageMaxWidth, maxTableWidth ?? NaN);
        }

        const { offsetWidth } = document.documentElement;
        const width =
            panelState.indexOf("pin") > -1
                ? offsetWidth - (isWidePanel ? WIDE_NAVIGATION_WIDTH : NAVIGATION_WIDTH)
                : offsetWidth;
        const minWidth = unitLayouting.paddingRight + unitLayouting.paddingLeft;

        if (width < minWidth) {
            return minWidth;
        }

        return Math.min(width, maxTableWidth ?? NaN);
    };

    private getHeightRows(isFirst?: boolean): undefined | number[] {
        const ieOffset: number = detectIE() && isFirst ? 1 : 0;

        if (this.dataBody == undefined) {
            return undefined;
        }
        const bodyRows = Array.from(this.dataBody.children);
        const sideRows = this.sidebarBody ? Array.from(this.sidebarBody.children) : [];
        if (bodyRows.length < 1) {
            return this.state.heightRows;
        }

        const heightRows: number[] = [];
        const rowsCount = Math.min(bodyRows.length, sideRows.length);

        for (let i = 0; i < rowsCount; i++) {
            const bodyRow = bodyRows[i];
            const sideRow = sideRows[i];
            if (!(bodyRow instanceof HTMLElement) || !(sideRow instanceof HTMLElement)) {
                continue;
            }
            let bodyHeight = bodyRow.offsetHeight;
            const sideHeight = sideRow.offsetHeight;

            if (sideRow.dataset.type === "multiline-row") {
                bodyHeight = sideHeight;
            }

            heightRows[i] = Math.max(bodyHeight, sideHeight) + ieOffset;
        }

        // @ts-ignore
        return heightRows.every((height, i) => this.state[i] === height) ? this.state.heightRows : heightRows;
    }
}
